import { Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { TableWrapper } from '@components';
import { LoadingButton } from '@mui/lab';
import { DetailedResourceNodeFieldsFragment, PermissionNodeFieldsFragment } from '@api/gql/graphql';
import { useFlagFixed } from '@hooks';
import { Flag } from '@utils';
import { RiskLevelImage } from '@common/components/RiskLevelImage';
import { riskLevelFields_Fragment } from '@AccessAlerts/services/gql';
import { getFragmentData } from '@api/gql';
import { RiskScore } from '@common/components/RiskScore';

type EntitlementsTableProps = {
  resource: DetailedResourceNodeFieldsFragment;
  permissions: PermissionNodeFieldsFragment[];
  fetchMore: () => void;
  loading: boolean;
  hasMore?: boolean;
};

export function EntitlementsTable({ resource, permissions, fetchMore, loading, hasMore }: EntitlementsTableProps) {
  const { isEnabled: isRiskScoreEnabled } = useFlagFixed(Flag.SHOW_RISK_SCORE);
  const resourceRiskLevel = getFragmentData(riskLevelFields_Fragment, resource.riskLevel);
  return (
    <Box pb={2}>
      <TableWrapper noBackground>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Resource</TableCell>
              <TableCell>Permission</TableCell>
              {isRiskScoreEnabled && <TableCell>Risk Score</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {permissions.map((permission) => (
              <EntitlementsTableRow
                permission={permission}
                resourceRiskLevel={resourceRiskLevel?.riskLevel}
                resource={resource}
                isRiskScoreEnabled={isRiskScoreEnabled}
                key={permission.id}
              />
            ))}
            {hasMore && (
              <TableRow>
                <TableCell colSpan={2}>
                  <Stack direction="row" justifyContent="center">
                    <LoadingButton loading={loading} onClick={() => fetchMore()} variant="text" size="small">
                      Load More
                    </LoadingButton>
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>
    </Box>
  );
}

function EntitlementsTableRow({
  isRiskScoreEnabled,
  resourceRiskLevel,
  resource,
  permission,
}: {
  isRiskScoreEnabled: boolean;
  resourceRiskLevel?: string;
  permissionRiskLevel?: string;
  permission: PermissionNodeFieldsFragment;
  resource: DetailedResourceNodeFieldsFragment;
}) {
  if (!isRiskScoreEnabled) {
    return (
      <TableRow key={permission.id}>
        <TableCell>{resource.name}</TableCell>
        <TableCell>{permission.name}</TableCell>
      </TableRow>
    );
  }
  const permissionRiskLevel = getFragmentData(riskLevelFields_Fragment, permission.riskLevel);
  return (
    <TableRow key={permission.id}>
      <TableCell>
        <ValueWithRiskLevel name={resource.name} riskLevel={resourceRiskLevel} />
      </TableCell>
      <TableCell>
        <ValueWithRiskLevel name={permission.name} riskLevel={permissionRiskLevel?.riskLevel} />
      </TableCell>
      <TableCell>
        {resourceRiskLevel && permissionRiskLevel ? (
          <RiskScore resourceRiskLevel={resourceRiskLevel} permissionRiskLevel={permissionRiskLevel?.riskLevel} />
        ) : (
          <></>
        )}
      </TableCell>
    </TableRow>
  );
}

function ValueWithRiskLevel({ name, riskLevel }: { name: string; riskLevel?: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      {riskLevel && <RiskLevelImage riskLevel={riskLevel} />}
      <Typography>{name}</Typography>
    </Stack>
  );
}
